// Saved audiences: named, reusable rule sets ("All admins of Acme").
import React, { useState } from "react";
import { Plus, Pencil, Trash2, Save, Users, Info, ListFilter } from "lucide-react";
import { useBroadcastPage } from "../context";
import { broadcastApi } from "../api";
import { Modal, Field, Btn, SectionCard, EmptyState, GroupTitle } from "./ui";
import RuleBuilder from "./RuleBuilder";
import AudienceReach from "./AudienceReach";
import { newRule, fromServerRules, toPayload, rulesReady, rulesSummary } from "../utils/rules";

const AudienceEditor = ({ audience, onClose, onSaved }) => {
  const { s, t, notify } = useBroadcastPage();
  const A = t.audiences;
  const [name, setName] = useState(audience?.name || "");
  const [description, setDescription] = useState(audience?.description || "");
  const [rules, setRules] = useState(() => (audience?.rules?.length ? fromServerRules(audience.rules) : [newRule("ROLE")]));
  const [busy, setBusy] = useState(false);

  const ready = rulesReady(rules);
  const save = async () => {
    setBusy(true);
    try {
      const body = { name: name.trim(), description: description.trim() || null, rules: toPayload(rules) };
      if (audience?.id) await broadcastApi.updateAudience(audience.id, body);
      else await broadcastApi.createAudience(body);
      onSaved();
      notify("success", A.saved(body.name));
    } catch (e) {
      notify("error", e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={audience?.id ? A.editTitle(audience.name) : A.newTitle}
      icon={Users}
      onClose={onClose}
      busy={busy}
      maxWidth={860}
      footer={
        <>
          <Btn kind="secondary" onClick={onClose} disabled={busy}>
            {t.cancel}
          </Btn>
          <Btn disabled={busy || !name.trim() || !ready} onClick={save}>
            <Save size={16} /> {busy ? t.saving : A.save}
          </Btn>
        </>
      }
    >
      <GroupTitle icon={Info} style={{ marginTop: 0 }}>
        {A.basics}
      </GroupTitle>
      <div style={s.grid2}>
        <Field label={`${A.name} *`}>
          <input className="bc-input" style={s.input} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} placeholder={A.namePlaceholder} />
        </Field>
        <Field label={A.description}>
          <input className="bc-input" style={s.input} value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </div>
      <GroupTitle icon={ListFilter}>{A.rules}</GroupTitle>
      <p style={{ ...s.hint, marginTop: -6, marginBottom: 14 }}>{A.rulesHint}</p>
      <RuleBuilder rules={rules} onChange={setRules} />
      <AudienceReach ready={ready} rules={toPayload(rules)} />
    </Modal>
  );
};

const AudiencesTab = () => {
  const { s, t, audiences, options, refreshAudiences, notify } = useBroadcastPage();
  const A = t.audiences;
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    const a = deleting;
    setBusy(true);
    try {
      await broadcastApi.deleteAudience(a.id);
      await refreshAudiences();
      setDeleting(null);
      notify("success", A.deleted(a.name));
    } catch (e) {
      notify("error", e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard
      icon={Users}
      title={A.title}
      subtitle={A.subtitle}
      actions={
        <Btn onClick={() => setEditing({})}>
          <Plus size={16} /> {A.newAudience}
        </Btn>
      }
    >
      {audiences.length === 0 ? (
        <EmptyState icon={Users}>{A.empty}</EmptyState>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table(640)}>
            <thead>
              <tr>
                <th style={s.th}>{A.colName}</th>
                <th style={s.th}>{A.colWho}</th>
                <th style={s.th}>{A.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {audiences.map((a) => (
                <tr key={a.id} className="bc-row">
                  <td style={s.td}>
                    <div style={s.cellTitle}>{a.name}</div>
                    {a.description && <div style={s.cellMeta}>{a.description}</div>}
                  </td>
                  <td style={{ ...s.td, color: s.colors.muted, fontSize: 13.5 }}>{rulesSummary(a.rules, options, t)}</td>
                  <td style={{ ...s.td, whiteSpace: "nowrap", width: 1 }}>
                    <div style={s.actionsCell}>
                      <button type="button" className="bc-action-btn" style={s.actionBtn()} onClick={() => setEditing(a)}>
                        <Pencil size={14} /> {t.edit}
                      </button>
                      <button type="button" className="bc-action-btn" style={s.actionBtn("danger")} onClick={() => setDeleting(a)}>
                        <Trash2 size={14} /> {t.delete}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <AudienceEditor
          audience={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refreshAudiences();
          }}
        />
      )}
      {deleting && (
        <Modal
          title={A.deleteTitle}
          icon={Trash2}
          onClose={() => setDeleting(null)}
          busy={busy}
          footer={
            <>
              <Btn kind="secondary" disabled={busy} onClick={() => setDeleting(null)}>
                {t.cancel}
              </Btn>
              <Btn kind="danger" disabled={busy} onClick={remove}>
                <Trash2 size={16} /> {t.delete}
              </Btn>
            </>
          }
        >
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7 }}>{A.deleteBody(deleting.name)}</p>
        </Modal>
      )}
    </SectionCard>
  );
};

export default AudiencesTab;
